import { createQueryFn } from '@oneview/redux/utils';
import { showToast } from '@/redux';
import FileSaver from 'file-saver';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export interface Document {
    id: number;
    authorId: string;
    name: string;
    description?: string;
    fileName: string;
    isNew: boolean;
    mimeType: string;
    createdAt: string;
    softDeletedAt: string | null;
}

export const profileApi = api.injectEndpoints({
    endpoints: (build) => ({
        getUserDocuments: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                    }: {
                        watchmenUserId: string;
                    },
                    { extra: api },
                ) => {
                    const { data } = await api.middlewareSdk.get<Document[]>('crm/v1/documents', undefined, {
                        params: {
                            watchmenUserId,
                        },
                    });

                    return data;
                },
            ),
            providesTags: [DataTags.GetClientDocuments],
        }),

        uploadUserDocuments: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                        file,
                        name,
                        description,
                    }: {
                        watchmenUserId: string;
                        file: File;
                        name: string;
                        description?: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        const formData = new FormData();
                        formData.set('file', file, name);
                        formData.set('name', name);
                        formData.set('description', description || '');
                        await api.middlewareSdk.post<Document[]>('crm/v1/documents', formData, {
                            params: {
                                watchmenUserId,
                            },
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        dispatch(showToast('File has been uploaded'));
                    } catch (error) {
                        dispatch(showToast('Failed to upload file'));
                        throw error;
                    }
                },
            ),
            invalidatesTags: [DataTags.GetClientDocuments],
        }),

        updateUserDocuments: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                        documentId,
                        name,
                        description,
                    }: {
                        watchmenUserId: string;
                        documentId: number;
                        name: string;
                        description?: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.middlewareSdk.put(
                            'crm/v1/documents/:documentId',
                            { name, description },
                            {
                                params: {
                                    watchmenUserId,
                                },
                                pathParams: {
                                    documentId,
                                },
                            },
                        );
                        dispatch(showToast('File has been updated'));
                    } catch (error) {
                        dispatch(showToast('Failed to update file'));
                        throw error;
                    }
                },
            ),
            invalidatesTags: [DataTags.GetClientDocuments],
        }),

        downloadUserDocument: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                        documentId,
                        fileName,
                    }: {
                        watchmenUserId: string;
                        documentId: number;
                        fileName: string;
                    },
                    { extra: api },
                ) => {
                    const { data } = await api.middlewareSdk.get<Blob>(
                        'crm/v1/documents/:documentId/download',
                        undefined,
                        {
                            params: {
                                watchmenUserId,
                            },
                            pathParams: {
                                documentId,
                            },
                            responseType: 'blob',
                        },
                    );

                    FileSaver.saveAs(data, fileName);
                },
            ),
        }),

        markUserDocumentsAsDeleted: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                        documentId,
                    }: {
                        watchmenUserId: string;
                        documentId: number;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.middlewareSdk.patch<Document[]>(
                            'crm/v1/documents/:documentId/mark-delete',
                            undefined,
                            {
                                params: {
                                    watchmenUserId,
                                },
                                pathParams: {
                                    documentId,
                                },
                            },
                        );
                        dispatch(showToast('File has been deleted'));
                    } catch (error) {
                        dispatch(showToast('Failed to delete file'));
                        throw error;
                    }
                },
            ),
            invalidatesTags: [DataTags.GetClientDocuments],
        }),

        markUserDocumentsAsUndeleted: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                        documentId,
                    }: {
                        watchmenUserId: string;
                        documentId: number;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.middlewareSdk.patch<Document[]>(
                            'crm/v1/documents/:documentId/mark-undelete',
                            undefined,
                            {
                                params: {
                                    watchmenUserId,
                                },
                                pathParams: {
                                    documentId,
                                },
                            },
                        );
                        dispatch(showToast('File has been restored'));
                    } catch (error) {
                        dispatch(showToast('Failed to restore file'));
                        throw error;
                    }
                },
            ),
            invalidatesTags: [DataTags.GetClientDocuments],
        }),

        markUserDocumentsAsSeen: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                    }: {
                        watchmenUserId: string;
                    },
                    { extra: api },
                ) => {
                    await api.middlewareSdk.patch<Document[]>('crm/v1/documents/mark-seen', undefined, {
                        params: {
                            watchmenUserId,
                        },
                    });
                },
            ),
            invalidatesTags: [DataTags.GetClientDocuments],
        }),
    }),
});

export const {
    useGetUserDocumentsQuery,
    useUploadUserDocumentsMutation,
    useLazyDownloadUserDocumentQuery,
    useMarkUserDocumentsAsDeletedMutation,
    useMarkUserDocumentsAsUndeletedMutation,
    useUpdateUserDocumentsMutation,
} = profileApi;
